import { Component, computed, inject, input, output } from '@angular/core';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { TableColumnsEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { collectSelectableRowIds } from '@app/shared/utils/time-report-table.utils';
import { ReportsDataTableComponent } from '../reports-data-table/reports-data-table.component';

const ALL_COLUMNS: TableColumnsEnum[] = [
  TableColumnsEnum.SELECT,
  TableColumnsEnum.DATE,
  TableColumnsEnum.USER,
  TableColumnsEnum.COMPANY,
  TableColumnsEnum.PROJECT,
  TableColumnsEnum.TASK_ID,
  TableColumnsEnum.TASK_TITLE,
  TableColumnsEnum.LOCATION,
  TableColumnsEnum.DESCRIPTION,
  TableColumnsEnum.TYPE,
  TableColumnsEnum.COMMENT,
  TableColumnsEnum.ESTIMATION,
  TableColumnsEnum.TOTAL,
  TableColumnsEnum.STATUS,
  TableColumnsEnum.ACTIONS
];

const MOBILE_COLUMNS: TableColumnsEnum[] = [
  TableColumnsEnum.DATE,
  TableColumnsEnum.USER,
  TableColumnsEnum.PROJECT,
  TableColumnsEnum.TYPE,
  TableColumnsEnum.TOTAL,
  TableColumnsEnum.ACTIONS
];

@Component({
  selector: 'proffeo-flat-reports-table',
  templateUrl: './flat-reports-table.component.html',
  styleUrl: './flat-reports-table.component.scss',
  imports: [ReportsDataTableComponent]
})
export class FlatReportsTableComponent {
  private readonly deviceService = inject(DeviceService);
  protected readonly isMobile = this.deviceService.isMobile;

  protected readonly columns = computed<TableColumnsEnum[]>(() => (this.isMobile() ? MOBILE_COLUMNS : ALL_COLUMNS));

  protected readonly allSelectableIds = computed(() => {
    const allowed = this.selectableReportIds();
    const rows = this.flattenedWorkTimeReports();
    return allowed === null
      ? collectSelectableRowIds(rows)
      : [...new Set(rows.flatMap(row => (row.id && allowed.includes(row.id) ? [row.id] : [])))];
  });

  public readonly selectedTimeReportIds = input<string[]>([]);
  public readonly selectableReportIds = input<readonly string[] | null>(null);
  public readonly selectionDisabled = input(false);

  public readonly flattenedWorkTimeReports = input.required<TimeReportRowVM[]>();

  public readonly selectedTimeReportIdsChanged = output<string[]>();
  public readonly editReport = output<TimeReport>();
  public readonly deleteReport = output<TimeReport>();
  public readonly taskDetailsOpened = output<string>();

  protected onSelectReport(id: string): void {
    if (this.selectionDisabled() || !this.allSelectableIds().includes(id)) return;
    const currentIds = this.selectedTimeReportIds().filter(item => this.allSelectableIds().includes(item));
    const newIds = currentIds.includes(id) ? currentIds.filter(item => item !== id) : [...currentIds, id];
    this.selectedTimeReportIdsChanged.emit(newIds);
  }

  protected onSelectAll(): void {
    if (this.selectionDisabled()) return;
    const allIds = this.allSelectableIds();
    const shouldDeselectAll = allIds.length > 0 && allIds.every(id => this.selectedTimeReportIds().includes(id));
    const newIds = shouldDeselectAll ? [] : allIds;
    this.selectedTimeReportIdsChanged.emit(newIds);
  }
}
